import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { Borrower } from "../../models/borrower-model";
import { Utils } from "../../services/utils";
import { PasswordPolicyResponse } from "../../models/auth/password-policy-response.model";
import { AuthService } from "../../services/auth.service";
import { NavigationService } from "../../services/navigation.service";
import { ConfigurableFieldHostComponent } from "../configurable-field-host.component";
import { applyPhoneNumber, derivePhoneType, PHONE_TYPES, phoneNumberFor, PhoneType } from "../../utils/borrower-phone";
declare const $: any;

@Component({
    selector: 'borrower-info',
    templateUrl: 'borrower-info.component.html',
    styleUrls: ['./borrower-info.component.scss'],
    standalone: false
})
export class BorrowerInfoComponent extends ConfigurableFieldHostComponent implements OnInit {

  @ViewChild('borrowerInfoForm') borrowerInfoForm: NgForm | undefined;

  @Input()
  borrower!: Borrower;

  @Input()
  doesSetPassword: boolean = false;

  // Bound by both hosts (create-account-step, create-coborrower-account); declared so the binding
  // is real instead of silently swallowed by CUSTOM_ELEMENTS_SCHEMA. Not rendered today.
  @Input()
  customMessage: string | undefined;

  password: string = "";
  confirmedPassword: string = "";

  passwordPolicy: PasswordPolicyResponse;

  // LLS-569 item 2: Phone Type is UI-only. The single phone input is bound to enteredPhone and
  // routed into the borrower's mobile/home/work slot by the helpers in utils/borrower-phone.ts.
  protected readonly phoneTypes: readonly PhoneType[] = PHONE_TYPES;
  phoneType: PhoneType = 'Mobile';
  enteredPhone: string = '';

  constructor(
    private readonly _authService: AuthService,
    private readonly _navigationService: NavigationService,
  ) {
    super();
  }

  passwordInputState = {
    id: 'password',
    displayed: false,
  };
  confirmPasswordInputState = {
    id: 'confirmedPassword',
    displayed: false,
  };

  protected passwordIsMinimum8Characters: boolean = false;
  protected passwordContainsDigit: boolean = false;
  protected passwordContainsUppercase: boolean = false;
  protected passwordContainsLowercase: boolean = false;
  protected passwordContainsSpecialCharacter: boolean = false;

  /** Phone Type only makes sense next to a visible Phone Number. */
  get showPhoneType(): boolean {
    return !this.isHidden('borrower.mobilePhone') && !this.isHidden('borrower.phoneType');
  }

  // ConfigurableFieldHost.isRequired() falls back to true for a key it cannot find; the phone was
  // never required on this step (and the co-borrower host passes no config), so fall back to false.
  phoneRequired = (): boolean => this.fieldConfig?.['borrower.mobilePhone']?.isRequired ?? false;
  phoneTypeRequired = (): boolean => this.fieldConfig?.['borrower.phoneType']?.isRequired ?? false;

  validate = () => {
    this.borrowerInfoForm?.form.markAllAsTouched();
    const isValid = this.borrowerInfoForm?.form.valid && this.isPasswordValid;
    let firstInvalidOneId: string = null;
    if (isValid) {
      return true;
    }

    for (const key in this.borrowerInfoForm?.form.controls) {
      if (Object.prototype.hasOwnProperty.call(this.borrowerInfoForm?.form.controls, key)) {
        if (this.borrowerInfoForm?.form.controls[key].status === 'INVALID') {
          firstInvalidOneId = key;
          break;
        }
      }
    }
    if (firstInvalidOneId) {
      this.scrollToElement(firstInvalidOneId);
    }
    return false;
  }

  scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }, 250);
    }
  };

  ngOnInit() {
    this.phoneType = derivePhoneType(this.borrower);
    this.enteredPhone = phoneNumberFor(this.borrower, this.phoneType);
    if (this.doesSetPassword && this._navigationService.companyGuid) {
      this.loadPasswordPolicy(this._navigationService.companyGuid);
    }
  }

  onPasswordChanged = () => {
    const minLength = this.passwordPolicy?.minimumPasswordLength || 8;
    this.passwordContainsDigit = Utils.containsDigit(this.password);
    this.passwordIsMinimum8Characters = this.password.trim().length >= minLength;
    this.passwordContainsUppercase = Utils.containsUppercase(this.password);
    this.passwordContainsLowercase = Utils.containsLowercase(this.password);
    this.passwordContainsSpecialCharacter = Utils.containsSpecialCharacter(
      this.password
    );
  };

  get isPasswordValid(): boolean {
    if (!this.doesSetPassword) return true;
    if (!this.password) return false;
    if (!this.passwordIsMinimum8Characters) return false;
    const p = this.passwordPolicy;
    if (!p) {
      return this.passwordContainsDigit && this.passwordContainsUppercase
        && this.passwordContainsLowercase && this.passwordContainsSpecialCharacter;
    }
    if (p.requireDigit && !this.passwordContainsDigit) return false;
    if (p.requireUppercase && !this.passwordContainsUppercase) return false;
    if (p.requireLowercase && !this.passwordContainsLowercase) return false;
    if (p.requireSpecialCharacter && !this.passwordContainsSpecialCharacter) return false;
    return true;
  }

  private loadPasswordPolicy(companyGuid: string): void {
    this._authService.getPasswordPolicyByCompanyGuid(companyGuid).subscribe({
      next: (policy) => {
        this.passwordPolicy = policy;
        this.onPasswordChanged();
      }
    });
  }

  onBorrowerPhoneChanged = (value: string | null | undefined) => {
    this.enteredPhone = value ?? '';
    applyPhoneNumber(this.borrower, this.phoneType, this.enteredPhone);
  }

  onPhoneTypeChanged = () => {
    applyPhoneNumber(this.borrower, this.phoneType, this.enteredPhone);
  }

  protected onPasswordStateClicked = (state: {id: string, displayed: boolean}) => {
    state.displayed = !state.displayed;
    const passwordElement = document.getElementById(state.id);
    passwordElement.setAttribute('type', state.displayed ? 'text' : 'password');
  }
}
